import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setFilters,
  setPage,
  setItemPerPage,
  fetchDataSummary,
  fetchExportData,
  selectDataSummary,
  selectIsLoading,
  selectPage,
  selectItemPerPage,
  selectTotalCount,
  selectFilters,
  selectIsExporting,
} from "../../redux/qr-credit-summary";
import DataSummaryComponent from "./data-summary-credit.component";
import {
  getMonthOptions,
  getYearOptions,
  formatDateForAPI,
} from "../../utils/date-utils";
import {
  exportToCSV,
  exportToTXT,
  exportToExcel,
} from "../../utils/export-utils";
import {
  filterModes,
  getInitialFilterMode,
  getDateRangeFromFilters,
} from "../../utils/filter-utils";

const exportMap = {
  csv: (data) => exportToCSV(data, "DataSummary.csv"),
  txt: (data) => exportToTXT(data, "DataSummary.txt"),
  excel: (data) => exportToExcel(data, "DataSummary.xlsx"),
};

export default function DataSummaryCreditContainer() {
  const dispatch = useDispatch();
  const data = useSelector(selectDataSummary);
  const isLoading = useSelector(selectIsLoading);
  const page = useSelector(selectPage);
  const itemPerPage = useSelector(selectItemPerPage);
  const totalCount = useSelector(selectTotalCount);
  const filters = useSelector(selectFilters);
  const isExporting = useSelector(selectIsExporting);
  const monthOptions = getMonthOptions();
  const yearOptions = getYearOptions();

  const [filterMode, setFilterMode] = useState(() =>
    getInitialFilterMode(filters)
  );

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleDateRangeChange = (dateRange) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      dispatch(
        setFilters({
          dateRangeStart: null,
          dateRangeEnd: null,
        })
      );
      return;
    }

    dispatch(
      setFilters({
        dateRangeStart: formatDateForAPI(dateRange[0]),
        dateRangeEnd: formatDateForAPI(dateRange[1]),
      })
    );
  };

  const handleModeChange = (newMode) => {
    setFilterMode(newMode);

    let newFilters = {
      dateRangeStart: null,
      dateRangeEnd: null,
    };

    switch (newMode) {
      case filterModes.YEAR:
        newFilters = {
          ...newFilters,
          month: "",
          fromDay: "",
          toDay: "",
        };
        break;

      case filterModes.MONTH:
        newFilters = {
          ...newFilters,
          fromDay: "",
          toDay: "",
          month: filters.month || monthOptions[new Date().getMonth()],
        };
        break;

      case filterModes.DAY: {
        const today = new Date();
        const defaultStart = new Date(today);
        defaultStart.setDate(defaultStart.getDate() - 13);
        newFilters = {
          ...newFilters,
          dateRangeStart: formatDateForAPI(defaultStart),
          dateRangeEnd: formatDateForAPI(today),
        };
        break;
      }

      default:
        console.warn(`Unexpected filter mode: ${newMode}`);
        setFilterMode(filterModes.YEAR);
        newFilters = {
          ...newFilters,
          month: "",
          fromDay: "",
          toDay: "",
        };
        break;
    }

    dispatch(setFilters(newFilters));
  };

  useEffect(() => {
    if (
      filterMode === filterModes.DAY &&
      filters.dateRangeStart &&
      filters.dateRangeEnd
    ) {
      dispatch(
        fetchDataSummary({
          dateRangeStart: filters.dateRangeStart,
          dateRangeEnd: filters.dateRangeEnd,
          page,
          length: itemPerPage,
        })
      );
    } else {
      dispatch(
        fetchDataSummary({
          year: filters.year,
          month: filters.month,
          fromDay: filters.fromDay,
          toDay: filters.toDay,
          page,
          length: itemPerPage,
        })
      );
    }
  }, [filters, page, itemPerPage, dispatch, filterMode]);

  const onPageChange = (newPage) => dispatch(setPage(newPage));
  const onItemsPerPageChange = (newItemPerPage) =>
    dispatch(setItemPerPage(newItemPerPage));

  const selectedDateRangeRef = React.useRef([null, null]);

  const selectedDateRange = React.useMemo(() => {
    if (filterMode === filterModes.DAY) {
      if (filters.dateRangeStart && filters.dateRangeEnd) {
        const startDate = new Date(filters.dateRangeStart);
        const endDate = new Date(filters.dateRangeEnd);
        selectedDateRangeRef.current = [startDate, endDate];
        return [startDate, endDate];
      }
    } else if (
      filters.year &&
      filters.month &&
      filters.fromDay &&
      filters.toDay
    ) {
      const range = getDateRangeFromFilters(
        filters.year,
        filters.month,
        filters.fromDay,
        filters.toDay,
        monthOptions
      );
      if (range) {
        selectedDateRangeRef.current = range;
        return range;
      }
    }
    return selectedDateRangeRef.current;
  }, [filters, filterMode, monthOptions]);

  const handleExport = async (format) => {
    const exportParams =
      filterMode === filterModes.DAY &&
      filters.dateRangeStart &&
      filters.dateRangeEnd
        ? {
            dateRangeStart: filters.dateRangeStart,
            dateRangeEnd: filters.dateRangeEnd,
          }
        : {
            year: filters.year,
            month: filters.month,
            fromDay: filters.fromDay,
            toDay: filters.toDay,
          };

    const resultAction = await dispatch(fetchExportData(exportParams));

    if (fetchExportData.fulfilled.match(resultAction)) {
      const dataToExport = resultAction.payload;
      exportMap[format]?.(dataToExport);
    } else {
      console.error("Export failed.");
    }
  };

  return (
    <DataSummaryComponent
      year={filters.year}
      setYear={(val) => handleFilterChange("year", val)}
      month={filters.month}
      setMonth={(val) => handleFilterChange("month", val)}
      fromDay={filters.fromDay}
      setFromDay={(val) => handleFilterChange("fromDay", val)}
      toDay={filters.toDay}
      setToDay={(val) => handleFilterChange("toDay", val)}
      yearOptions={yearOptions}
      monthOptions={monthOptions}
      data={data}
      isDataSummaryLoading={isLoading}
      handleExport={handleExport}
      isExporting={isExporting}
      dataSummaryPage={page}
      onDataSummaryPageClick={onPageChange}
      itemPerPage={itemPerPage}
      enableItemPerPageOption
      handleItemPerPageChange={onItemsPerPageChange}
      totalQrCreditCount={totalCount}
      filterMode={filterMode}
      onModeChange={handleModeChange}
      selectedDateRange={selectedDateRange}
      onDateRangeChange={handleDateRangeChange}
    />
  );
}
